import React, { useState, useRef, useEffect } from "react";
import { FaBell } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { typeToIcon, useNotifications } from "@/contexts/NotificationsContext";
import { timeAgo } from "@/utils/helpers";
import toast from "react-hot-toast";
import NotificationsBadge from "../NotificationsBadge";
import { usePushPermissionWarning } from "@/contexts/PushPermissionContext";
import { Capacitor } from "@capacitor/core";
import { Notification } from "@/types/globalTypes";

const Notifications: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const {
    unreadedNotifications,
    markAsRead,
    handleMarkAsReadWithSearch,
    markAsReadWithNavigate,
  } = useNotifications();
  const { showWarning } = usePushPermissionWarning();

  const toggleDropdown = () => setIsOpen((prevState) => !prevState);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      bellRef.current &&
      !bellRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleUnreadedNotification = async (notification: Notification) => {
    if (notification.type === "order-received") {
      await handleMarkAsReadWithSearch(notification)
        .then(() => setIsOpen(false))
        .catch(() => toast.error(t("notification.updateError") as string));
    } else if (
      notification.type === "subscription-day-alarm" ||
      notification.type === "subscription-month-alarm" ||
      notification.type === "subscription-week-alarm" ||
      notification.type === "subscription-expired"
    ) {
      await markAsReadWithNavigate(notification, "/settings/subscribtion")
        .then(() => setIsOpen(false))
        .catch(() => toast.error(t("notification.updateError") as string));
    } else if (notification.type === "order-reviewed") {
      await markAsReadWithNavigate(
        notification,
        `/orders/reviews?id=${notification.meta?.orderId}`,
      )
        .then(() => setIsOpen(false))
        .catch(() => toast.error(t("notification.updateError") as string));
    } else {
      markAsRead([notification.id] as string[]);
    }
  };

  return (
    <div ref={bellRef} className="relative">
      <button
        onClick={toggleDropdown}
        className="group relative rounded-full p-2 duration-300 hover:bg-black/10"
      >
        <FaBell className="text-lg text-white md:text-2xl md:text-gray-500 md:group-hover:text-primary" />
        {/* Badge for unread notifications */}

        {showWarning && Capacitor.getPlatform() !== "web" ? (
          <>
            <span
              className={`absolute right-[2px] top-[2px] inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 opacity-75 md:right-0 md:top-0`}
            />
            <span
              className={`absolute right-[2px] top-[2px] inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white md:right-0 md:top-0`}
            >
              !
            </span>
          </>
        ) : (
          unreadedNotifications.length > 0 && (
            <NotificationsBadge
              length={unreadedNotifications.length}
              parentClassNames="bg-red-500"
              childClassNames="bg-red-500"
              xAxis="right"
              position="absolute"
            />
          )
        )}
      </button>

      {/* Dropdown menu */}
      {isOpen && !showWarning && (
        <div
          ref={dropdownRef}
          className={`absolute ${i18n.language === "en" ? "right-0" : "left-0"} z-[9999] mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg`}
        >
          <div className="flex items-center justify-between border-b border-gray-200 p-3 font-semibold">
            <div className="text-gray-700">{t("notification.title")}</div>

            <button
              onClick={() => markAsRead()}
              className="rounded-md bg-sidebar px-4 py-2 text-xs font-semibold text-skyblue"
            >
              {t("notification.markAllAsRead")}
            </button>
          </div>
          <div className="thin-scrollbar max-h-64 w-full overflow-y-auto overflow-x-hidden pr-2">
            {unreadedNotifications.length > 0 ? (
              unreadedNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleUnreadedNotification(notification)}
                  className={`${notification.type === "order-received" || notification.type === "order-reviewed" ? "cursor-pointer" : "cursor-default"} p-3 hover:bg-gray-100 ${notification.read ? "bg-white" : "bg-sky-50"}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      {/* icon */}
                      {typeToIcon[notification.type]}
                      <span className="break-words pe-5 text-sm">
                        {t(notification.titleEN, notification.titleAR)}
                      </span>
                    </div>
                    {/* time */}
                    <span className="self-start text-nowrap text-xs text-gray-800">
                      {timeAgo(notification.time * 1000, lang)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {t(notification.contentEN, notification.contentAR)}
                  </p>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500">
                {t("notification.noNew")}
              </div>
            )}
          </div>

          {/* View More Button */}
          <Link
            to="/notifications"
            onClick={() => setIsOpen(false)}
            className="inline-block w-full rounded-b-md border-t border-gray-200 px-3 py-2 text-center text-sm hover:bg-black/10"
          >
            {t("notification.viewAll")}
          </Link>
        </div>
      )}
    </div>
  );
};

export default Notifications;
